// @ts-strict-ignore
import { ComplexProjectVO, ProjectHeadVO, ProjectVO } from '../model/vo/project.vo';
import { ProjectModel } from '../model/project.model';
import { ProjectDataService } from './project-data.service';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ArticleUtils } from '@article/util/article-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { UserUtils } from '@user/service/user-utils';
import { OverlayBackground } from '@shared/overlay/overlay.type';
import { OverlayService } from '@shared/overlay/overlay.service';
import { UserModel } from '@user/model/user.model';
import { AlertButton } from '@shared/alert/alertButton';
import { AlertHelper } from '@shared/alert/alert.helper';
import { NumberUtils } from '@shared/util/number-utils';
import { ErrorInfoVO } from '@shared/vo/error-info.vo';
import { ErrorUtils } from '@shared/util/error-utils';
import { ProjectUtils } from '../components/project/util/project-utils';
import { StringUtils } from '@shared/util/string-utils';
import { InitManager } from '@shared/manager/init.manager';
import { LoadProjectArticlesService } from './load-project-articles.service';
import { LoadProjectDrillingHolesService } from './load-project-drilling-holes.service';
import { LayerManager } from '@3d-scene/manager/layer-manager';
import { ProjectGroupVO } from '../model/vo/project-group.vo';
import { ProjectTypeValidator } from '../components/project/service/project-type.validator';
import { Injectable } from '@angular/core';
import { RouteNavigationService } from '@services/route-navigation.service';
import { ProjectModelService } from './project-model.service';
import { UserSettings } from '@user/model/user-settings';
import { SelectionModelService } from '../components/article/service/selection-model.service';
import { GlobalContainerService } from '@3d-scene/model3d/global-container.service';
import { LoadProjectInfoService } from './load-project-info.service';
import { ProjectService } from '../components/import-export/service/project.service';
import { Log } from '@shared/debug/log';
import { ProjectComponentPresenter } from '../components/project-component.presenter';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { LoadedProjectData } from './loaded-project.data';
import { CorruptedDataService } from './corrupted-data.service';
import { CorruptArticleAction } from '../components/article/project-loading-corrupt-articles/project-loading-corrupt-articles.component';
import { RemoveProjectArticleService } from './remove-project-article.service';
import { LoadConnectionTextureProcessor } from '@3d-scene/processor/load-connection-texture.processor';
import { ContainerService } from '@3d-scene/model3d/container.service';

@Injectable({ providedIn: 'root' })
export class LoadProjectService {

  constructor(
    private loadProjectInfoService: LoadProjectInfoService,
    private userModel: UserModel,
    private userSettings: UserSettings,
    private projectService: ProjectService,
    private projectDataService: ProjectDataService,
    private projectModel: ProjectModel,
    private projectModelService: ProjectModelService,
    private overlayService: OverlayService,
    private alertHelper: AlertHelper,
    private loadProjectArticlesService: LoadProjectArticlesService,
    private loadProjectDrillingHolesService: LoadProjectDrillingHolesService,
    private layerManager: LayerManager,
    private projectTypeValidator: ProjectTypeValidator,
    private selectionModelService: SelectionModelService,
    private globalContainerService: GlobalContainerService,
    private routeNavigationService: RouteNavigationService,
    private projectComponentPresenter: ProjectComponentPresenter,
    private corruptedDataService: CorruptedDataService,
    private removeProjectArticleService: RemoveProjectArticleService,
    private loadConnectionTextureProcessor: LoadConnectionTextureProcessor,
    private containerService: ContainerService,
  ) {}

  public async loadProjectById(projectId: number, projectVersion: number = -1): Promise<LoadedProjectData> {
    try {
      const loadedProjectData = await this.overlayService.doWithOverlay(async () => {
        await this.projectModelService.clearCurrentProject();
        const complexProjectVO = await this.projectDataService.loadComplexProject(projectId, projectVersion);
        complexProjectVO.isArticleProject = false;
        return await this.loadingProjectSuccess(complexProjectVO);
      }, OverlayBackground.color);
      await this.handleCorruptedArticles();
      return loadedProjectData;
    } catch (error) {
      return this.loadingProjectFailure(error);
    }
  }

  public async loadProjectByGuid(guid: string) : Promise<void> {
    await this.projectModelService.clearCurrentProject();
    const complexProjectVO = await this.projectDataService.loadComplexProjectByGuid(guid);
    complexProjectVO.isArticleProject = false;
    await this.loadingProjectSuccess(complexProjectVO);
  }

  public async loadProject(projectHead: ProjectHeadVO, isCompositeProject: boolean): Promise<void> {
    if (projectHead.isLocked && UserUtils.isWagoUser(this.userModel.user)) {
      const isProductionUser: boolean = this.userModel.user && this.userModel.user.isProduction();
      await this.projectService.showProjectLockedMessage(isProductionUser);
      if (isProductionUser) {
        return;
      }
    }
    if (await this.checkConfirmLoad()) {
      await this.continueLoading(projectHead, isCompositeProject);
    }
  }

  public async checkConfirmLoad(): Promise<boolean> {
    if (this.projectModel.currentProjectRequiresSaving) {
      const answer = await this.alertHelper.showAlert('load.title.confirm_load_project', 'load.message.confirm_load_project', null,
        [AlertButton.YES, AlertButton.NO], "error");
      return answer === AlertButton.YES;
    } else {
      return true;
    }
  }

  private async continueLoading(projectHead: ProjectHeadVO, isCompositeProject: boolean): Promise<any> {
    const version: number = NumberUtils.isNotNumber(projectHead.currentVersion) ? -1 : projectHead.currentVersion;
    if (isCompositeProject) {
      await this.loadCompositeArticleComplexProject(projectHead.id, version);
    } else {
      await this.loadProjectById(projectHead.id, version);
    }
  }

  private getOutdatedArticles(complexProjectVO: ComplexProjectVO): ArticleVO[] {
    const outdatedArticles: ArticleVO[] = complexProjectVO.articles
      .filter((a) => ArticleUtils.isArticleOutdated(a));

    if (complexProjectVO.isArticleProject) {
      // loading an article project, consider all articles.
      return outdatedArticles;
    }

    // loading a user-project, ignore composite part articles
    const isCompositeMap = ProjectUtils.articleUsages(complexProjectVO.project)
      .reduce(
        (map, articleUsage) => map.set(articleUsage.articleId, ArticleUsageUtils.isCompositePartArticleUsage(articleUsage)),
        new Map<number, boolean>());

    return outdatedArticles.filter((a) => !isCompositeMap.get(a.identity));
  }

  private async loadingProjectSuccess(complexProjectVO: ComplexProjectVO, isCompositeArticle: boolean = false): Promise<LoadedProjectData> {
    const outdatedArticles: ArticleVO[] = this.getOutdatedArticles(complexProjectVO);
    if (ArrayUtils.isNotEmpty(outdatedArticles) || ArticleUtils.isStatusOutdated(complexProjectVO.project.materialStatus)) {
      const infoValues = {
        outdatedArticles: outdatedArticles,
        configurationName: complexProjectVO.project.name,
        materialstatus: complexProjectVO.project.materialStatus,
        lastCallDate: complexProjectVO.project.lastCall,
        isWagoUser: UserUtils.isWagoUser(this.userModel.user),
      };
      this.projectComponentPresenter.showOutdatedArticlesDialog(infoValues);
    }
    return await this.loadProjectModel(complexProjectVO, isCompositeArticle);
  }

  public async loadProjectModel(complexProjectVO: ComplexProjectVO, isCompositeArticle: boolean = false): Promise<LoadedProjectData> {
    this.loadProjectInfoService.isLoading = true;
    this.projectModelService.checkAndClearAllArticleObject3DData(complexProjectVO.project);
    this.loadProjectArticlesService.updateInfoProperties(complexProjectVO.project);
    this.userSettings.saveLastSelectedProjectFromComplexProjectVO(complexProjectVO);
    await this.completeComplexProjectVOandSetCurrentProject(complexProjectVO);
    try {
      await this.loadProjectArticlesService.loadProjectArticles(complexProjectVO, isCompositeArticle);
      this.checkProjectOptionDataVersion();
      this.loadProjectDrillingHolesService.checkStartDrillingHolePosition();
      this.projectModel.corruptArticles = this.corruptedDataService.findCorruptProjectData();
      if (!this.routeNavigationService.isAtConfigurator()) {
        await this.routeNavigationService.toConfigurator();
      }
      this.activateConstructionModeLayers();
      this.loadProjectInfoService.isLoading = false;
      return this.loadProjectInfoService.projectLoadedSuccess(complexProjectVO);
    } catch (error) {
      this.loadProjectInfoService.isLoading = false;
      return this.loadProjectInfoService.projectLoadedError(error);
    }
  }

  public async handleCorruptedArticles(): Promise<void> {
    if (this.projectModel.corruptArticles.length > 0) {
      const action = await this.projectComponentPresenter.showCorruptArticlesDialog();
      if (action === CorruptArticleAction.DELETE) {
        const articleUsageIds: string[] = this.projectModel.corruptArticles.map((corruptArticle) => corruptArticle.articleUsage.id);
        await this.removeProjectArticleService.removeArticles(articleUsageIds);
        await this.loadConnectionTextureProcessor.loadAndApplyTexturesToArticleContainers(
          this.containerService.enumerateArticleContainersInGlobal()
        );
        this.projectModel.corruptArticles = [];
      }
    }
  }

  private activateConstructionModeLayers() {
    if (this.userModel.isConstruction) {
      this.layerManager.activate('dimensions', 'articleNumbers');
    }
  }

  private async completeComplexProjectVOandSetCurrentProject(complexProjectVO : ComplexProjectVO): Promise<void> {
    await this.loadProjectArticlesService.setArticleInfos(complexProjectVO);
    this.projectModel.setArticleUsagesCaches(complexProjectVO.project);
    this.selectionModelService.removeAllSelections();
    this.globalContainerService.updateProjectModel(complexProjectVO.project);

    this.projectTypeValidator.validateProjectType(complexProjectVO.project);
    await this.projectModelService.setComplexProject(complexProjectVO);
    if (this.projectModel.isCurrentProjectConfigurationGroup) {
      const project: ProjectVO = this.projectModel.currentProject;
      const configurationGroup: ProjectGroupVO = project.projectGroup;
      if (!configurationGroup.projects) {
        configurationGroup.projects = [];
      }
      const projectIds: number[] = configurationGroup.projects.map((project: ProjectVO) => project.id);
      let projectExists: boolean = false;
      for (const id of projectIds) {
        if (id === project.id || (isNaN(id) && isNaN(project.id))) {
          projectExists = true;
        }
      }
      if (!projectExists) {
        configurationGroup.projects.push(project);
      }
    }
  }

  private loadingProjectFailure(error: any): LoadedProjectData {
    let projectLoadedError = this.loadProjectInfoService.projectLoadedError(error);

    // show project loading failed message
    const errorInfo: ErrorInfoVO = ErrorUtils.getErrorInfo(error);
    this.showLoadingError(errorInfo.message);
    return projectLoadedError;
  }

  private showLoadingError(errorInfoMessage: string): void {
    const errorInfoMessageDetail: string = Log.isDevMode ? errorInfoMessage : '';
    this.alertHelper.showError('load.title.load_failed', 'load.message.load_failed', {translationParams: [errorInfoMessageDetail]});
  }

  private async loadCompositeArticleComplexProject(projectId: number, version: number = -1): Promise<void> {
    try {
      await this.overlayService.doWithOverlay(async () => {
        const complexProjectVO = await this.projectDataService.loadCompositeArticleComplexProject(projectId, version);
        complexProjectVO.isArticleProject = true;
        await this.loadingProjectSuccess(complexProjectVO, true);
      }, OverlayBackground.color);
    } catch (error) {
      this.loadingProjectFailure(error);
    }
  }
  private checkProjectOptionDataVersion(): void {
    if (this.projectModel.currentProject) {
      const projectOptionDataVersionString = this.projectModel.currentInfoProperties.getString("projectOptionDataVersion");

      let projectOptionDataVersion: number = -1;
      if (!StringUtils.isEmpty(projectOptionDataVersionString)) {
        projectOptionDataVersion = parseFloat(projectOptionDataVersionString);
      }
      if (
        projectOptionDataVersion === -1 ||
        NumberUtils.isNotNumber(projectOptionDataVersion) ||
        projectOptionDataVersion < parseFloat(InitManager.FSF_NORM_PROJECT_OPTION_DATA_VERSION)
      ) {
        if (!UserUtils.isWagoUser(this.userModel.userProfile.user)) {
          this.alertHelper
            .showAlert('load.title.old_project_option_data', 'load.message.old_project_option_data', null, [AlertButton.OK], "info")
            .then(() => {
              if (
                this.userModel.userProfile &&
                this.userModel.userProfile.user &&
                this.userModel.userProfile.user.hasPermission("LOAD_PROJECT_OPTION_DEFINITIONS")
              ) {
                this.projectComponentPresenter.openEditConfigurationSettingsByUser();
              }
            });
        }
      }
    }
  }
}
